import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { Select, TextField } from '@ui/inputs';
import { SkeletonButton } from '@ui/buttons';
import { skeletonPaths } from '@features/scoreSpec/jsonPath';
import { Consequence, MicroLabel, Row } from '@features/scoreSpec/styles';
import { ROW_TYPES, OBJECT_TYPE } from './schemaModel';
import {
  setFieldName,
  setFieldType,
  setFieldDescription,
  setFieldEnum,
  addField,
  removeField,
  setScore,
  addScore,
  removeScore,
  setColumn,
  addColumn,
  removeColumn,
} from './schemaEdits';
import { pathHint, isBadPath } from './contractProblems';
import { ContractJsonPane } from './ContractJsonPane';

// The shape editor.
//
// Rows, not JSON, because a rename has to be visible AS a rename — see schemaEdits.js. Every
// control here routes through a verb in that module and never rebuilds the model itself, so the
// path rewriting and the refusals cannot be bypassed by a control that means well.
//
// The path control is a pick-from-shape select over `skeletonPaths`, the same construction the
// advanced editor uses (OutPanel.js:108-109). A path that resolves to nothing is therefore mostly
// unreachable rather than merely reported — which matters more here than there, because this screen
// exists for operators who are not reading the whole spec.

const OTHER = '__other__';

const FieldRowGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 128px auto;
  gap: 8px;
  align-items: end;
`;

const Nested = styled.div`
  padding-left: 12px;
  border-left: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const RowAction = styled.button`
  height: 38px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  background: transparent;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.themeColors.text};
    border-color: ${({ theme }) => theme.themeColors.border};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }
`;

const TYPE_OPTIONS = ROW_TYPES.map((type) => ({ value: type, label: type }));

// Free text is not an escape from validation, it is an escape from the SUBSET the checker can
// evaluate: `parseJsonPath` models the dotted / bracketed / indexed forms and abstains on the rest
// (jsonPath.js:1-14). A path outside it has to remain typeable, or a spec using one could never be
// edited here at all.
const PathControl = ({ id, label, ariaLabel, value, onChange, schema, exactlyOne }) => {
  const pickable = React.useMemo(() => (schema ? skeletonPaths(schema) : []), [schema]);
  const isPickable = pickable.indexOf(value) > -1;
  const [wantsCustom, setWantsCustom] = React.useState(false);
  const custom = wantsCustom || (!!value && !isPickable);

  const options = pickable
    .map((path) => ({ value: path, label: path }))
    .concat([{ value: OTHER, label: 'Type a path…' }]);

  return (
    <div>
      <Select
        id={id}
        label={label}
        aria-label={ariaLabel}
        value={custom ? OTHER : value}
        placeholder={value ? undefined : 'Pick from the shape…'}
        options={options}
        invalid={isBadPath(value, schema, exactlyOne)}
        hint={custom ? undefined : pathHint(value, schema, exactlyOne)}
        onChange={(next) => {
          if (next === OTHER) {
            setWantsCustom(true);
            return;
          }
          setWantsCustom(false);
          onChange(next);
        }}
      />
      {custom && (
        <Container top={8}>
          <TextField
            aria-label={`${ariaLabel} — typed`}
            mono
            value={value}
            onChange={onChange}
            invalid={isBadPath(value, schema, exactlyOne)}
            hint={pathHint(value, schema, exactlyOne)}
          />
        </Container>
      )}
    </div>
  );
};

// The fixed-value list, and why it holds its own text.
//
// `enum` is the one constraint strict mode enforces, so it is the most valuable thing a converted
// prose contract can carry — a categorical score becomes grammar-constrained instead of hoped for.
//
// The text is held rather than derived from the members on every render: parsing `up, ` to ["up"] and
// rendering it back as "up" would eat the comma the operator just typed.
const EnumField = ({ row, qualified, apply, text, onText }) => (
  <TextField
    aria-label={`Fixed values for ${qualified || 'this field'}`}
    mono
    value={text === undefined ? (row.enumValues || []).join(', ') : text}
    placeholder="bullish, neutral, bearish"
    onChange={(next) => {
      onText(next);
      apply((m) =>
        setFieldEnum(
          m,
          row.id,
          next
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v !== '')
        )
      );
    }}
    hint="Comma-separated, and enforced: the model cannot answer anything outside this list. Leave empty for no constraint."
  />
);

// `qualified` comes from the caller rather than being derived here: a shape with a top-level `Score`
// and a `Peers.Score` would otherwise give two controls the same accessible name.
// A row's optional keys — `description` and `enum` — are revealed rather than always present. Three
// controls on every row for two keys the shipped specs mostly do not use would cost more height than
// they earn, and the shape editor is read as much as it is edited.
const FieldRow = ({ row, apply, depth, qualified, enumText, onEnumText, descriptions, onOpenDescription }) => (
  <Container top={depth === 0 ? 16 : 8}>
    <FieldRowGrid>
      <TextField
        aria-label={`Answer field name${qualified ? ` — ${qualified}` : ''}`}
        mono
        value={row.name}
        placeholder="FieldName"
        onChange={(name) => apply((m) => setFieldName(m, row.id, name))}
      />
      <Select
        aria-label={`Type of ${qualified || 'the new field'}`}
        value={row.type}
        options={TYPE_OPTIONS}
        onChange={(type) => apply((m) => setFieldType(m, row.id, type))}
      />
      <RowAction type="button" onClick={() => apply((m) => removeField(m, row.id))}>
        Remove
      </RowAction>
    </FieldRowGrid>

    {/* A description is part of the contract the model is held to — strict mode passes it through to
        the request, so it is instruction, not documentation. Multiline because that is what it is
        used for: a sentence telling the model what the field means, not a label. Available on an
        object row too, where it describes the section rather than a value. */}
    {(row.description || descriptions[row.id]) && (
      <Container top={8}>
        <TextField
          aria-label={`Description of ${qualified || 'this field'}`}
          multiline
          rows={2}
          value={row.description}
          placeholder="What this field means, in the model's terms."
          onChange={(description) => apply((m) => setFieldDescription(m, row.id, description))}
          hint="Sent to the model as part of the schema. Guidance a schema cannot hold — a rubric, a numeric range — still belongs in the prompt."
        />
      </Container>
    )}

    {row.type !== OBJECT_TYPE && (row.enumValues || enumText[row.id] !== undefined) && (
      <Container top={8}>
        <EnumField
          row={row}
          qualified={qualified}
          apply={apply}
          text={enumText[row.id]}
          onText={(next) => onEnumText(row.id, next)}
        />
      </Container>
    )}

    {/* Whichever optional keys this row does not have yet, on one line rather than stacked. */}
    {(!(row.description || descriptions[row.id]) ||
      (row.type !== OBJECT_TYPE && !(row.enumValues || enumText[row.id] !== undefined))) && (
      <Container top={8}>
        <Row>
          {/* The button label is qualified, like the aria-labels: a shape with a top-level `Score` and
              a `Peers.Score` would otherwise put two identically-named buttons on screen. */}
          {!(row.description || descriptions[row.id]) && (
            <RowAction type="button" onClick={() => onOpenDescription(row.id)}>
              {`Describe ${qualified || 'this field'}`}
            </RowAction>
          )}
          {row.type !== OBJECT_TYPE && !(row.enumValues || enumText[row.id] !== undefined) && (
            <RowAction type="button" onClick={() => onEnumText(row.id, '')}>
              Fixed values
            </RowAction>
          )}
        </Row>
      </Container>
    )}

    {row.type === OBJECT_TYPE && (
      <Container top={8}>
        <Nested>
          {(row.children || []).map((child) => (
            <FieldRow
              key={child.id}
              row={child}
              apply={apply}
              depth={depth + 1}
              qualified={row.name ? `${row.name}.${child.name}` : child.name}
              enumText={enumText}
              onEnumText={onEnumText}
              descriptions={descriptions}
              onOpenDescription={onOpenDescription}
            />
          ))}
          <Container top={8}>
            <SkeletonButton onClick={() => apply((m) => addField(m, row.id))}>{`Add a field inside ${
              row.name || 'this object'
            }`}</SkeletonButton>
          </Container>
        </Nested>
      </Container>
    )}
  </Container>
);

export const ContractEditor = ({ contract }) => {
  const { model, schema, applyEdit: apply, patch } = contract;
  // Held per row, and only while the control is open — see EnumField.
  const [enumText, setEnumTextState] = React.useState({});
  const setEnumText = (rowId, text) => setEnumTextState((prev) => ({ ...prev, [rowId]: text }));
  // Which rows have been asked for a description. A row that already has one needs no entry; this is
  // only the "revealed but still empty" state, which nothing in the model can represent.
  const [descriptions, setDescriptionsState] = React.useState({});
  const openDescription = (rowId) => setDescriptionsState((prev) => ({ ...prev, [rowId]: true }));

  if (contract.useRaw) {
    return <ContractJsonPane contract={contract} />;
  }

  return (
    <div>
      <MicroLabel>The answer shape</MicroLabel>
      {model.rows.map((row) => (
        <FieldRow
          key={row.id}
          row={row}
          apply={apply}
          depth={0}
          qualified={row.name}
          enumText={enumText}
          onEnumText={setEnumText}
          descriptions={descriptions}
          onOpenDescription={openDescription}
        />
      ))}
      <Container top={16}>
        <Row>
          <SkeletonButton onClick={() => apply((m) => addField(m, null))}>Add a field</SkeletonButton>
          {/* The pane opens on what the ROWS currently say, not on the seed — otherwise switching to JSON
              would silently discard every field edit made so far. */}
          <SkeletonButton onClick={() => patch({ useRaw: true, rawSchema: schema })}>Edit as JSON</SkeletonButton>
        </Row>
      </Container>

      <Container top={24}>
        <MicroLabel>What extraction reads out of it</MicroLabel>
        {(model.scores || []).map((score) => (
          <Container top={16} key={score.id}>
            <TextField
              aria-label={`Score column name${score.name ? ` — ${score.name}` : ''}`}
              label="Score column"
              mono
              value={score.name}
              onChange={(name) => apply((m) => setScore(m, score.id, { name }))}
              hint="Lowercase letters, digits and underscores. It becomes a feed suffix and is permanent at lock — a field rename never moves it."
            />
            <Container top={8}>
              <PathControl
                id={`score-path-${score.id}`}
                label="reads"
                ariaLabel={`Path read by score ${score.name || '(unnamed)'}`}
                value={score.jsonpath}
                onChange={(jsonpath) => apply((m) => setScore(m, score.id, { jsonpath }))}
                schema={schema}
                exactlyOne
              />
            </Container>
            <Container top={8}>
              <RowAction type="button" onClick={() => apply((m) => removeScore(m, score.id))}>
                Remove this score
              </RowAction>
            </Container>
          </Container>
        ))}
        <Container top={16}>
          <SkeletonButton onClick={() => apply((m) => addScore(m))}>Add a score</SkeletonButton>
        </Container>
      </Container>

      <Container top={24}>
        <MicroLabel>Extra columns</MicroLabel>
        <Container top={8}>
          <Body7 isLowContrast>
            Broadcast: each one appears on every feed this spec publishes, alongside the score. They are payload, not
            signals — a path that matches nothing is recorded as null and the document still scores.
          </Body7>
        </Container>
        {(model.extraColumns || []).map((column) => (
          <Container top={16} key={column.id}>
            <FieldRowGrid>
              <TextField
                aria-label={`Column name${column.name ? ` — ${column.name}` : ''}`}
                mono
                value={column.name}
                placeholder="column_name"
                onChange={(name) => apply((m) => setColumn(m, column.id, { name }))}
              />
              <span />
              <RowAction type="button" onClick={() => apply((m) => removeColumn(m, column.id))}>
                Remove
              </RowAction>
            </FieldRowGrid>
            <Container top={8}>
              <PathControl
                id={`column-path-${column.id}`}
                label="reads"
                ariaLabel={`Path read by column ${column.name || '(unnamed)'}`}
                value={column.jsonpath}
                onChange={(jsonpath) => apply((m) => setColumn(m, column.id, { jsonpath }))}
                schema={schema}
              />
            </Container>
          </Container>
        ))}
        <Container top={16}>
          <SkeletonButton onClick={() => apply((m) => addColumn(m))}>Add a column</SkeletonButton>
        </Container>
      </Container>

      {contract.blocked && (
        <Container top={16}>
          <Consequence $tone="bad">{contract.blocked}</Consequence>
        </Container>
      )}
    </div>
  );
};
